import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Link,
  TextField,
  Button,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useThemeSettings } from '../../../../contexts/ThemeContext';
import { locationsApi, devicesApi } from '../../../../services/apiClient';
import StatusChip from '../shared/StatusChip';
import UrgencyChip from '../shared/UrgencyChip';
import SupportTypeChip from '../shared/SupportTypeChip';
import { formatDate } from '../../utils/ticketTransformers';
import { SUPPORT_TYPE_OPTIONS } from '../../utils/ticketConstants';

/**
 * Ticket information sidebar with editable fields for admins
 */
const TicketSidebar = ({
  ticket,
  isSupport,
  onStatusChange,
  onUrgencyChange,
  onLocationChange,
  onDeviceChange,
  onNextStepChange,
  onSupportTypeChange
}) => {
  const { t } = useThemeSettings();
  const navigate = useNavigate();

  // Edit mode states
  const [editingLocation, setEditingLocation] = useState(false);
  const [editingDevice, setEditingDevice] = useState(false);
  const [editingNextStep, setEditingNextStep] = useState(false);

  // Data states
  const [locations, setLocations] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);

  // Form states
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [nextStepValue, setNextStepValue] = useState(ticket?.next_step || '');

  // Load locations when editing location
  useEffect(() => {
    if (editingLocation && locations.length === 0) {
      fetchLocations();
    }
  }, [editingLocation]);

  // Load devices when location changes
  useEffect(() => {
    const locationId = selectedLocation?.id || ticket?.locationId || ticket?.location?.id;
    if (editingDevice && locationId) {
      fetchDevices(locationId);
    }
  }, [editingDevice, selectedLocation]);

  // Initialize selected values from ticket
  useEffect(() => {
    if (ticket?.location) {
      setSelectedLocation({
        id: ticket.location.id,
        name: ticket.location.friendlyName || ticket.location.name
      });
    }
    if (ticket?.related_device_id && ticket?.related_device_name) {
      setSelectedDevice({
        id: ticket.related_device_id,
        name: ticket.related_device_name
      });
    }
    setNextStepValue(ticket?.next_step || '');
  }, [ticket]);

  const fetchLocations = async () => {
    try {
      setLoadingLocations(true);
      const response = await locationsApi.getAll();
      let locationsData = [];
      if (response?.data) {
        if (Array.isArray(response.data)) {
          locationsData = response.data;
        } else if (response.data.data) {
          locationsData = response.data.data;
        } else if (response.data.locations) {
          locationsData = response.data.locations;
        }
      }

      const flatLocations = locationsData.map(loc => ({
        id: loc.id || loc._id,
        name: loc.friendlyName || loc.name || `Location ${loc.id || loc._id}`,
        locationType: loc.locationType || loc.location_type
      }));

      setLocations(flatLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchDevices = async (locationId) => {
    try {
      setLoadingDevices(true);
      let response;

      // Try to get devices by location first
      if (devicesApi.getByLocation) {
        try {
          response = await devicesApi.getByLocation(locationId);
        } catch {
          response = await devicesApi.getAll();
        }
      } else {
        response = await devicesApi.getAll();
      }

      let devicesData = [];
      if (response?.data) {
        if (Array.isArray(response.data)) {
          devicesData = response.data;
        } else if (response.data.data) {
          devicesData = response.data.data;
        } else if (response.data.devices) {
          devicesData = response.data.devices;
        }
      }

      // Filter devices by location if we got all devices
      const filteredDevices = devicesData
        .filter(device => {
          const deviceLocationId = device.deviceLocation || device.locationId || device.location_id || device.location?.id;
          return !locationId || String(deviceLocationId) === String(locationId);
        })
        .map(device => ({
          id: device.id || device._id,
          name: device.friendlyName || device.name || `Device ${device.id || device._id}`
        }));

      setDevices(filteredDevices);
    } catch (error) {
      console.error('Error fetching devices:', error);
      setDevices([]);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleLocationClick = () => {
    const locationId = ticket.locationId || ticket.location?.id;
    if (locationId) {
      navigate(`/locations/${locationId}`);
    }
  };

  const handleDeviceClick = () => {
    if (ticket.related_device_id) {
      navigate(`/devices?selectedId=${ticket.related_device_id}`);
    }
  };

  const handleSaveLocation = () => {
    if (selectedLocation && onLocationChange) {
      onLocationChange(selectedLocation.id, selectedLocation.name);
    }
    setEditingLocation(false);
  };

  const handleSaveDevice = () => {
    if (onDeviceChange) {
      onDeviceChange(selectedDevice?.id || null, selectedDevice?.name || null);
    }
    setEditingDevice(false);
  };

  const handleSaveNextStep = () => {
    if (onNextStepChange) {
      onNextStepChange(nextStepValue);
    }
    setEditingNextStep(false);
  };

  const handleCancelLocation = () => {
    setSelectedLocation(ticket?.location ? {
      id: ticket.location.id,
      name: ticket.location.friendlyName || ticket.location.name
    } : null);
    setEditingLocation(false);
  };

  const handleCancelDevice = () => {
    setSelectedDevice(ticket?.related_device_id ? {
      id: ticket.related_device_id,
      name: ticket.related_device_name
    } : null);
    setEditingDevice(false);
  };

  const handleCancelNextStep = () => {
    setNextStepValue(ticket?.next_step || '');
    setEditingNextStep(false);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" component="h3" gutterBottom>
        {t ? t("Tickets.ticket_information") : "Ticket Information"}
      </Typography>

      {/* Status */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t ? t("DeviceTable.status") : "Status"}
        </Typography>
        {isSupport ? (
          <FormControl fullWidth size="small">
            <Select
              value={ticket.status || 'Open'}
              onChange={(e) => onStatusChange(e.target.value)}
              renderValue={(value) => <StatusChip status={value} />}
            >
              <MenuItem value="Open"><StatusChip status="Open" /></MenuItem>
              <MenuItem value="In Progress"><StatusChip status="In Progress" /></MenuItem>
              <MenuItem value="Closed"><StatusChip status="Closed" /></MenuItem>
            </Select>
          </FormControl>
        ) : (
          <StatusChip status={ticket.status} />
        )}
      </Box>

      {/* Urgency */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t ? t("VisualTreeView.urgency") : "Urgency"}
        </Typography>
        {isSupport ? (
          <FormControl fullWidth size="small">
            <Select
              value={ticket.urgency || 'MEDIUM'}
              onChange={(e) => onUrgencyChange(e.target.value)}
              renderValue={(value) => <UrgencyChip urgency={value} />}
            >
              <MenuItem value="LOW"><UrgencyChip urgency="LOW" /></MenuItem>
              <MenuItem value="MEDIUM"><UrgencyChip urgency="MEDIUM" /></MenuItem>
              <MenuItem value="HIGH"><UrgencyChip urgency="HIGH" /></MenuItem>
              <MenuItem value="URGENT"><UrgencyChip urgency="URGENT" /></MenuItem>
            </Select>
          </FormControl>
        ) : (
          <UrgencyChip urgency={ticket.urgency} />
        )}
      </Box>

      {/* Support Type */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t ? t("Tickets.support_type") : "Support Type"}
        </Typography>
        {isSupport && onSupportTypeChange ? (
          <FormControl fullWidth size="small">
            <Select
              value={ticket.support_type || 'other'}
              onChange={(e) => onSupportTypeChange(e.target.value)}
              renderValue={(value) => <SupportTypeChip type={value} />}
            >
              {SUPPORT_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <SupportTypeChip type={opt.value} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <SupportTypeChip type={ticket.support_type} />
        )}
      </Box>

      {/* Location - Editable for support */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t ? t("Tickets.location") : "Location"}
          </Typography>
          {isSupport && !editingLocation && (
            <Button
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => setEditingLocation(true)}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              {t ? t("Common.edit") : "Edit"}
            </Button>
          )}
        </Box>

        {editingLocation ? (
          <Box>
            <Autocomplete
              size="small"
              options={locations}
              getOptionLabel={(option) => option.name || ''}
              value={selectedLocation}
              onChange={(e, newValue) => setSelectedLocation(newValue)}
              loading={loadingLocations}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t ? t("Tickets.select_location") : "Select location..."}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingLocations ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon fontSize="small" />}
                onClick={handleSaveLocation}
              >
                {t ? t("Common.save") : "Save"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CancelIcon fontSize="small" />}
                onClick={handleCancelLocation}
              >
                {t ? t("Common.cancel") : "Cancel"}
              </Button>
            </Box>
          </Box>
        ) : (
          ticket.location?.friendlyName || ticket.location?.name ? (
            <Link
              component="button"
              variant="body2"
              onClick={handleLocationClick}
              sx={{ textAlign: 'left' }}
            >
              {ticket.location.friendlyName || ticket.location.name}
            </Link>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t ? t("Common.not_specified") : "Not specified"}
            </Typography>
          )
        )}
      </Box>

      {/* Device - Editable for support */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t ? t("Tickets.device") : "Device"}
          </Typography>
          {isSupport && !editingDevice && (
            <Button
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => setEditingDevice(true)}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              {t ? t("Common.edit") : "Edit"}
            </Button>
          )}
        </Box>

        {editingDevice ? (
          <Box>
            <Autocomplete
              size="small"
              options={devices}
              getOptionLabel={(option) => option.name || ''}
              value={selectedDevice}
              onChange={(e, newValue) => setSelectedDevice(newValue)}
              loading={loadingDevices}
              noOptionsText={
                !ticket.locationId && !ticket.location?.id
                  ? (t ? t("Tickets.select_location_first") : "Select a location first")
                  : (t ? t("Tickets.no_devices_found_this_location") : "No devices at this location")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t ? t("Tickets.select_device") : "Select device..."}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingDevices ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon fontSize="small" />}
                onClick={handleSaveDevice}
              >
                {t ? t("Common.save") : "Save"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CancelIcon fontSize="small" />}
                onClick={handleCancelDevice}
              >
                {t ? t("Common.cancel") : "Cancel"}
              </Button>
            </Box>
          </Box>
        ) : (
          ticket.related_device_name ? (
            <Link
              component="button"
              variant="body2"
              onClick={handleDeviceClick}
              sx={{ textAlign: 'left' }}
            >
              {ticket.related_device_name}
            </Link>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t ? t("Common.not_specified") : "Not specified"}
            </Typography>
          )
        )}
      </Box>

      {/* Next Step - Editable for support */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t ? t("Tickets.next_step") : "Next Step"}
          </Typography>
          {isSupport && !editingNextStep && (
            <Button
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => setEditingNextStep(true)}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              {t ? t("Common.edit") : "Edit"}
            </Button>
          )}
        </Box>

        {editingNextStep ? (
          <Box>
            <TextField
              size="small"
              fullWidth
              multiline
              rows={3}
              value={nextStepValue}
              onChange={(e) => setNextStepValue(e.target.value)}
              placeholder={t ? t("Tickets.define_next_step_this_ticket") : "Define the next step for this ticket..."}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon fontSize="small" />}
                onClick={handleSaveNextStep}
              >
                {t ? t("Common.save") : "Save"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CancelIcon fontSize="small" />}
                onClick={handleCancelNextStep}
              >
                {t ? t("Common.cancel") : "Cancel"}
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color={ticket.next_step ? 'text.primary' : 'text.secondary'}>
            {ticket.next_step || (t ? t("Common.not_specified") : "Not specified")}
          </Typography>
        )}
      </Box>

      {/* Created By */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t ? t("Tickets.created_by") : "Created By"}
        </Typography>
        <Typography variant="body2">
          {ticket.createdByUser?.name ||
           `${ticket.createdByUser?.firstName || ''} ${ticket.createdByUser?.lastName || ''}`.trim() ||
           'Unknown'}
        </Typography>
      </Box>

      {/* Created At */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t ? t("Tickets.created") : "Created"}
        </Typography>
        <Typography variant="body2">
          {formatDate(ticket.created_at || ticket.createdAt)}
        </Typography>
      </Box>

      {/* Updated At */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t ? t("Tickets.last_updated") : "Last Updated"}
        </Typography>
        <Typography variant="body2">
          {formatDate(ticket.updated_at || ticket.updatedAt)}
        </Typography>
      </Box>

      {/* Support User */}
      {ticket.supportUser && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t ? t("Tickets.assigned_to") : "Assigned To"}
          </Typography>
          <Typography variant="body2">
            {ticket.supportUser.name ||
             `${ticket.supportUser.firstName || ''} ${ticket.supportUser.lastName || ''}`.trim() ||
             'Unknown'}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default TicketSidebar;
